// src/components/Sidebar.js
import { useEffect, useState } from "react";
import { getUserInfoFromToken } from "../services/auth";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Settings,
  UserCheck,
  Users2,
  FileText,
  MessageSquare,
  MessageCircle,
  CreditCard,
  Star,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  X,
  Radio,
  UserCog,
} from "lucide-react";

const Sidebar = ({ isMobileOpen, onMobileClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const userInfo = getUserInfoFromToken();
  const { userName } = userInfo || {};
  const role = userInfo?.role || null;
  const [isMobile, setIsMobile] = useState(false);
  const [chatsUnread, setChatsUnread] = useState(0);

  useEffect(() => {
    const update = () => setChatsUnread(window.__chatsUnread || 0);
    window.addEventListener("chats_unread_updated", update);
    return () => window.removeEventListener("chats_unread_updated", update);
  }, []);

  // collapsed is only active on desktop
  const collapsed = isCollapsed && !isMobile;

  // Handle scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileOpen, isMobile]);

  // Detect screen size to toggle mobile view
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      if (!isMobileView && isMobileOpen) {
        onMobileClose();
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileOpen, onMobileClose]);

  const navItems =
    role === "superadmin"
      ? [
          { label: "Admin Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
          { label: "User Management", path: "/admin/users", icon: Users },
          { label: "Subscription Requests", path: "/admin/subscription-requests", icon: ClipboardList },
          { label: "System Settings", path: "/admin/settings", icon: Settings },
        ]
      : role === "agent"
      ? [
          { label: "Dashboard", path: "/Dashboard", icon: LayoutDashboard },
          { label: "Chats", path: "/chats", icon: MessageCircle },
          { label: "Customer Details", path: "/customerDetails", icon: UserCheck },
          { label: "Group List", path: "/groupList", icon: Users2 },
          { label: "Template List", path: "/templateList", icon: FileText },
          { label: "Message History", path: "/messageHistory", icon: MessageSquare },
        ]
      : [
          // admin (workspace admin)
          { label: "Dashboard", path: "/Dashboard", icon: LayoutDashboard },
          { label: "Customer Details", path: "/customerDetails", icon: UserCheck },
          { label: "Group List", path: "/groupList", icon: Users2 },
          { label: "Template List", path: "/templateList", icon: FileText },
          { label: "Broadcast", path: "/broadcast", icon: Radio },
          { label: "Agents", path: "/agents", icon: UserCog },
          { label: "Message History", path: "/messageHistory", icon: MessageSquare },
          { label: "Chats", path: "/chats", icon: MessageCircle },
          { label: "Business Profile", path: "/business-profile", icon: Briefcase },
          { label: "Subscription Plans", path: "/subscription-plans", icon: CreditCard },
          { label: "My Subscription", path: "/subscription-management", icon: Star },
        ];

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed md:sticky top-0 left-0 h-full bg-gray-900 text-gray-100
          transform transition-all duration-300 ease-in-out z-50
          ${collapsed ? "w-16" : "w-64"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 touch-none flex-shrink-0 flex flex-col
        `}
      >
        {/* Header */}
        <div className={`border-b border-gray-700 ${collapsed ? "p-3" : "p-5"}`}>
          {collapsed ? (
            // Collapsed header — just the expand button centered
            <div className="flex justify-center">
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
                title="Expand sidebar"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            // Expanded header
            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold tracking-wide text-white">
                  WA Promotion
                </h2>
                <div className="flex items-center gap-1">
                  {/* Collapse button — desktop only */}
                  {!isMobile && (
                    <button
                      onClick={onToggleCollapse}
                      className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
                      title="Collapse sidebar"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  )}
                  {/* Close button — mobile only */}
                  {isMobile && (
                    <button
                      onClick={onMobileClose}
                      className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
                      aria-label="Close menu"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              {userName && (
                <div className="mt-2">
                  <p className="text-sm text-gray-300">
                    Welcome back,{" "}
                    <span className="font-medium">{userName}</span>!
                  </p>
                  {role === "superadmin" && (
                    <p className="text-xs text-blue-400 font-medium">Super Admin</p>
                  )}
                  {role === "admin" && (
                    <p className="text-xs text-green-400 font-medium">Admin</p>
                  )}
                  {role === "agent" && (
                    <p className="text-xs text-yellow-400 font-medium">Agent</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overscroll-contain min-h-0">
          <ul className={`space-y-1 ${collapsed ? "p-2" : "p-4"}`}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.path && location.pathname === item.path;
              const itemClasses = `
                flex items-center rounded-lg transition-all duration-200
                ${isActive ? "bg-blue-600 text-white" : "text-gray-300 hover:bg-gray-800 hover:text-white"}
                ${collapsed ? "justify-center px-2 py-3" : "px-4 py-3"}
              `;

              return (
                <li key={item.label}>
                  {item.path ? (
                    <Link
                      to={item.path}
                      className={itemClasses}
                      onClick={isMobile ? onMobileClose : undefined}
                      title={item.label}
                    >
                      <Icon
                        className={`w-5 h-5 flex-shrink-0 ${!collapsed ? "mr-3" : ""}`}
                      />
                      {!collapsed && (
                        <span className="truncate flex-1">{item.label}</span>
                      )}
                      {item.path === "/chats" && chatsUnread > 0 && (
                        <span className="ml-auto bg-green-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-medium flex-shrink-0">
                          {chatsUnread > 99 ? "99+" : chatsUnread}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <button
                      onClick={item.action}
                      className={`${itemClasses} w-full text-left`}
                      title={item.label}
                    >
                      <Icon
                        className={`w-5 h-5 flex-shrink-0 ${!collapsed ? "mr-3" : ""}`}
                      />
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className={`mt-auto border-t border-gray-700 ${collapsed ? "p-3" : "p-4"}`}>
          {collapsed ? (
            <p className="text-xs text-gray-600 text-center">WA</p>
          ) : (
            <p className="text-sm text-gray-500 text-center">
              © 2025 WA Promotion
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
